// admin/src/services/seedSystemRoles.ts
//
// Phase 2 — Seed the four system role docs into a newly-created organisation.
// Called from OrganizationManagement.tsx immediately after addDoc(organizations).
// The super-admin doing the create has permission to write role docs in any org
// via the isSuperAdmin() bypass in the role-doc create rule.
//
// Idempotent: writes use setDoc which will overwrite an existing doc with the
// same content. Re-running this on an already-seeded org is safe.
//
// Note on shared/system-roles.mjs: TypeScript (moduleResolution:bundler,
// strict:true) cannot auto-pair system-roles.d.ts → system-roles.mjs because
// the shared/ directory sits outside the admin tsconfig include path and the
// .mjs extension has no parallel .d.mts file that bundler-mode auto-discovers.
// Rather than patching tsconfig (which would affect Vite path resolution) the
// four frozen constants are mirrored inline here. They are derived from
// shared/system-roles.mjs at Phase 2 ship and must remain in sync with it.

import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { SystemRoleKey } from '../../../shared/system-roles.d.ts';

// ---------------------------------------------------------------------------
// Frozen system-role constants — mirrors shared/system-roles.mjs.
// These are FROZEN at Phase 2 ship. Do not edit without also updating
// shared/system-roles.mjs and the seed script (scripts/seedSystemRoles.mjs).
// ---------------------------------------------------------------------------

const SYSTEM_ROLE_KEYS: readonly SystemRoleKey[] = ['org_admin', 'manager', 'student', 'coach'];

const SYSTEM_ROLE_NAMES: Record<SystemRoleKey, string> = {
  org_admin: 'Org Admin',
  manager: 'Manager',
  student: 'Student',
  coach: 'Coach',
};

const SYSTEM_ROLE_DESCRIPTIONS: Record<SystemRoleKey, string> = {
  org_admin: 'Built-in administrator role for an organisation. Cannot be edited.',
  manager: 'Built-in manager role. Cannot be edited.',
  student: 'Built-in student role. Cannot be edited.',
  coach: 'Built-in coach role. Cannot be edited.',
};

const SYSTEM_ROLE_PERMISSIONS: Record<SystemRoleKey, string[]> = {
  org_admin: [
    'org-settings:read',
    'dashboard-config:write',
    // Phase 3.5 PR 4 — dashboard-config:read
    'dashboard-config:read',
    'teams:create',
    'teams:write',
    // Phase 3.5 PR 1 — teams:read + teams:list (new keys, JIT catalog growth)
    'teams:read',
    'teams:list',
    'coaches:write',
    'students:invite',
    'students:write',
    // Phase 3 PR 2 — scenarios:read (new key)
    'scenarios:read',
    'scenarios:create',
    'scenarios:write',
    'scenarios:delete',
    'scenarios:assign',
    'scenarios:pin',
    // Phase 3.5 PR 3 — org-featured-scenarios (manager: read + write)
    'org-featured-scenarios:read',
    'org-featured-scenarios:write',
    'student-groups:read',
    'student-groups:create',
    'student-groups:write',
    'student-groups:delete',
    // Phase 3 PR 1 — scenario-assignments
    'scenario-assignments:list',
    'scenario-assignments:read',
    'scenario-assignments:create',
    'scenario-assignments:write',
    'scenario-assignments:delete',
    // Phase 3.5 PR 5 — courses:read (new key, JIT catalog growth)
    'courses:read',
    'courses:create',
    'courses:write',
    'courses:delete',
    'courses:assign-students',
    // Phase 3.5 PR 5 — scenario_templates RBAC keys (manager: read/create/write/delete)
    'scenario-templates:read',
    'scenario-templates:create',
    'scenario-templates:write',
    'scenario-templates:delete',
    // Phase 3.5 PR 6 — course_modules + module_scenarios RBAC keys
    'course-modules:read',
    'course-modules:create',
    'course-modules:write',
    'course-modules:delete',
    'module-scenarios:read',
    'module-scenarios:create',
    'module-scenarios:write',
    'module-scenarios:delete',
    'sessions:read-as-manager',
    // Phase 3 PR 3 — reflections:read-as-manager (mirrors sessions:read-as-manager)
    'reflections:read-as-manager',
    'coaching-sessions:read',
    'coaching-sessions:write',
    'feedbacks:read-as-manager',
    'feedbacks:review',
    'analytics:read',
    'reports:read',
    'reports:create',
    'broadcasts:send',
    'course-progress:read-as-manager',
    'audit-logs:read',
    // Phase 3.5 PR 2 — notifications CREATE keys
    'notifications:create',
    'manager-notifications:create',
    'student-notifications:create',
    // orgAdminOnly
    'org-settings:write',
    'org-dpa:write',
    'teams:delete',
    'coaches:delete',
    'managers:invite',
    'managers:write',
    'user-consents:read-as-admin',
    // Phase 3.5 PR 5 — org-admin power-up keys for courses + scenario_templates
    'courses:write-any',
    'courses:delete-any',
    'scenario-templates:write-any',
    'scenario-templates:delete-any',
    // Phase 4 — org-admin role-management permissions
    'org-roles:read',
    'org-roles:write',
    'org-roles:delete',
  ],
  manager: [
    'org-settings:read',
    'dashboard-config:write',
    // Phase 3.5 PR 4 — dashboard-config:read
    'dashboard-config:read',
    'teams:create',
    'teams:write',
    // Phase 3.5 PR 1 — teams:read + teams:list (new keys, JIT catalog growth)
    'teams:read',
    'teams:list',
    'coaches:write',
    'students:invite',
    'students:write',
    // Phase 3 PR 2 — scenarios:read (new key)
    'scenarios:read',
    'scenarios:create',
    'scenarios:write',
    'scenarios:delete',
    'scenarios:assign',
    'scenarios:pin',
    // Phase 3.5 PR 3 — org-featured-scenarios (manager: read + write)
    'org-featured-scenarios:read',
    'org-featured-scenarios:write',
    'student-groups:read',
    'student-groups:create',
    'student-groups:write',
    'student-groups:delete',
    // Phase 3 PR 1 — scenario-assignments
    'scenario-assignments:list',
    'scenario-assignments:read',
    'scenario-assignments:create',
    'scenario-assignments:write',
    'scenario-assignments:delete',
    // Phase 3.5 PR 5 — courses:read (new key, JIT catalog growth)
    'courses:read',
    'courses:create',
    'courses:write',
    'courses:delete',
    'courses:assign-students',
    // Phase 3.5 PR 5 — scenario_templates RBAC keys (manager: read/create/write/delete)
    'scenario-templates:read',
    'scenario-templates:create',
    'scenario-templates:write',
    'scenario-templates:delete',
    // Phase 3.5 PR 6 — course_modules + module_scenarios RBAC keys
    'course-modules:read',
    'course-modules:create',
    'course-modules:write',
    'course-modules:delete',
    'module-scenarios:read',
    'module-scenarios:create',
    'module-scenarios:write',
    'module-scenarios:delete',
    'sessions:read-as-manager',
    // Phase 3 PR 3 — reflections:read-as-manager (mirrors sessions:read-as-manager)
    'reflections:read-as-manager',
    'coaching-sessions:read',
    'coaching-sessions:write',
    'feedbacks:read-as-manager',
    'feedbacks:review',
    'analytics:read',
    'reports:read',
    'reports:create',
    'broadcasts:send',
    'course-progress:read-as-manager',
    'audit-logs:read',
    // Phase 3.5 PR 2 — notifications CREATE keys
    'notifications:create',
    'manager-notifications:create',
    'student-notifications:create',
    // Note: courses:write-any, courses:delete-any, scenario-templates:write-any,
    // scenario-templates:delete-any are org-admin only — NOT included for manager.
  ],
  student: [
    'org-settings:read',
    'sessions:read-own',
    'reflections:read-own',
    'reflections:write-own',
    'feedbacks:read-own',
    'student-groups:join-via-email',
    // Phase 3 PR 3 — student-owned sessions + reflections RBAC keys
    'sessions:create-own',
    'sessions:write-own',
    'reflections:create-own',
    // Phase 3 PR 4 — student-owned feedbacks RBAC keys
    'feedbacks:create-own',
    'feedbacks:write-own',
    // Phase 3.5 PR 3 — org-featured-scenarios read (students render pinned list)
    'org-featured-scenarios:read',
    // Phase 3.5 PR 4 — dashboard-config read (students read coaching tips + nested resources)
    'dashboard-config:read',
  ],
  coach: [
    'sessions:read-own',
    'reflections:read-own',
    'reflections:write-own',
    'feedbacks:read-own',
  ],
};

// ---------------------------------------------------------------------------

export async function seedSystemRoles(orgId: string): Promise<void> {
  for (const roleKey of SYSTEM_ROLE_KEYS) {
    const ref = doc(db, `organizations/${orgId}/roles/${roleKey}`);
    const existing = await getDoc(ref);
    await setDoc(ref, {
      roleKey,
      name: SYSTEM_ROLE_NAMES[roleKey],
      description: SYSTEM_ROLE_DESCRIPTIONS[roleKey],
      permissions: [...SYSTEM_ROLE_PERMISSIONS[roleKey]],
      isSystem: true,
      createdAt: existing.exists() ? existing.data().createdAt : serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: null,
      updatedBy: 'admin-app:seedSystemRoles',
    });
  }
}
